import {
  Box, Button, Typography, useTheme,
} from '@mui/material';
import React from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import analytics from '../../../utils/analytics';
import SetPassword from './SetPassword';
import { ProductType } from '../../../types/account';
import { getPreloanPath } from '../../../utils/navigateToPreloan';

type LoanRequestStatusProps = {
  errorCode: number;
  origin: string;
  success: boolean;
  email: string;
  token: string;
  hasAccount: boolean;
  productType: ProductType;
}

export default function LoanRequestStatus(props: LoanRequestStatusProps) {
  const {
    errorCode, origin, success, email, token, hasAccount, productType,
  } = props;
  const theme = useTheme();
  const [openAlertDialog, setOpenAlertDialog] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<{
    icon: React.ReactNode;
    title: string;
    subTitle: React.ReactNode;
    button: { label: string; href?: string; action?:() => void };
    pageName: string;
      } | null>(null);

  const getErrorMessage = () => {
    if (success) {
      return {
        icon: (
          <>
            <img width="90px" src="https://assets.banca.me/icons/10.png" alt="icon" />
            <CheckCircleOutlineIcon style={{
              color: 'green',
              paddingTop: '20px',
              fontSize: '60px',
            }}
            />
          </>
        ),
        title: '¡Estás Listo!',
        subTitle: (
          <>
            { !['downPayment', 'mortgage'].includes(productType) && (
              <>
                <Typography variant="h5">
                  Prepárate para recibir el dinero en la misma cuenta donde suscribiste
                  tu PAC dentro de las
                  {' '}
                  <b>próximas 12 horas.</b>
                </Typography>
                <br />
              </>
            )}
            <Typography>
              Te hemos enviado una copia del contrato a tu correo
              {' '}
              <b>{email}</b>
              <br />
              <br />
              { !hasAccount ? (
                <>
                  Crea tu contraseña para acceder a tu cuenta
                  {' '}
                  <Typography component="span" color="primary" sx={{ fontWeight: 'bold' }}>banca.me</Typography>
                  {' '}
                  donde podrás ver los detalles de tu crédito.
                </>
              ) : (
                <>
                  Inicia sesión en tu cuenta
                  {' '}
                  <Typography component="span" color="primary" sx={{ fontWeight: 'bold' }}>banca.me</Typography>
                  {' '}
                  para ver los detalles de tu crédito.
                </>
              )}
            </Typography>
          </>
        ),
        button: hasAccount ? { label: 'Ir a mi cuenta', href: '/account/login' } : { label: 'Crear contraseña', action: () => setOpenAlertDialog(true) },
        pageName: hasAccount ? 'LR FIRMADO USUARIO' : 'LR FIRMADO USUARIO NUEVO',
      };
    }
    switch (errorCode) {
      case 307:
        return {
          icon: (
            <>
              <img width="100px" src="https://assets.banca.me/icons/10.png" alt="icon" />
              <HighlightOffIcon style={{
                color: theme.palette.error.main,
                paddingTop: '20px',
                fontSize: '60px',
              }}
              />
            </>
          ),
          title: 'Tiempo de solicitud agotado',
          subTitle: (
            <Typography>
              Por favor, presiona
              {' '}
              <b>Reintentar</b>
              {' '}
              para continuar con la solicitud.
            </Typography>
          ),
          button: {
            label: 'Reintentar',
            action: () => {
              // @ts-ignore
              window.location = window.location.href;
            },
          },
          pageName: 'TOKEN EXPIRADO',
        };
      case 400:
        return {
          icon: (
            <>
              <img width="100px" src="https://assets.banca.me/icons/10.png" alt="icon" />
              <HighlightOffIcon style={{
                color: theme.palette.error.main,
                paddingTop: '20px',
                fontSize: '60px',
              }}
              />
            </>
          ),
          title: 'Esta solicitud se encuentra cancelada',
          subTitle: (
            <>
              <Typography variant="h5">¡Estaremos felices en recibir otra solicitud tuya!</Typography>
              <br />
              <Typography>¡Si tienes alguna duda, contáctanos!</Typography>
            </>
          ),
          button: { label: 'Ir al inicio', href: getPreloanPath(origin) },
          pageName: 'LR CANCELADO',
        };
      case 412:
        return {
          icon:
  <img width="100px" src="https://assets.banca.me/icons/9.png" alt="icon" />,
          title: '¡Ya tienes un crédito activo con nosotros!',
          subTitle: (
            <>
              <Typography variant="h6">
                Debió haberte llegado
                {' '}
                <b>un correo de confirmación.</b>
                <br />
                Puedes acceder a los detalles de tu crédito en tu
                {' '}
                <b>cuenta de usuario.</b>
              </Typography>
              <br />
              <Typography>Si piensas que hay un problema, por favor contáctanos.</Typography>
            </>
          ),
          button: { label: 'Ir a mi cuenta', href: '/account' },
          pageName: 'CREDITO ACTIVO',
        };
      case 413:
        return {
          icon: (
            <>
              <img width="90px" src="https://assets.banca.me/icons/10.png" alt="icon" />
              <CheckCircleOutlineIcon style={{
                color: 'green',
                paddingTop: '20px',
                fontSize: '60px',
              }}
              />
            </>
          ),
          title: '¡Solicitud de crédito firmada!',
          subTitle: (
            <>
              <Typography variant="h5">
                Al momento de ser aprobada tu solicitud,
                {' '}
                <b>te llegará un  mail de confirmación.</b>
              </Typography>
              <br />
              <Typography>
                Si piensas que hay un problema, por favor contáctanos.
              </Typography>
            </>
          ),
          button: { label: 'Ir a mi cuenta', href: '/account/login' },
          pageName: 'LR FIRMADO',
        };
      default:
        return {
          icon: <img width="50px" src="https://assets.banca.me/isotipo_dark_light.png" alt="icon" />,
          title: '¡Ha ocurrido un error!',
          subTitle: (
            <>
              <Typography variant="h6" component="div">
                Por favor, intenta nuevamente más tarde.
              </Typography>
              <br />
              <Typography>
                Si el problema persiste, contáctanos.
              </Typography>
            </>
          ),
          button: { label: 'Ir al inicio', href: getPreloanPath(origin) },
          pageName: 'ERROR',
        };
    }
  };

  React.useEffect(() => {
    const errorMessageObj = getErrorMessage();
    setErrorMessage(errorMessageObj);
    if (success) {
      analytics.page('LR - FIRMA', errorMessageObj.pageName);
    } else {
      analytics.page('LR - REDIRECCION', errorMessageObj.pageName, {
        errorCode,
      });
    }
  }, [errorCode]);

  React.useEffect(() => {
    if (openAlertDialog) {
      setTimeout(() => {
        setOpenAlertDialog(false);
      }, 500);
    }
  }, [openAlertDialog]);

  if (!errorCode || !errorMessage) {
    return null;
  }
  return (
    <div style={{ textAlign: 'center', alignContent: 'center' }}>
      <div style={{
        maxWidth: '500px',
      }}
      >
        {errorMessage.icon}
        <h1>{errorMessage.title}</h1>
        <Box sx={{ placeSelf: 'center', textAlign: 'center', paddingBottom: 5 }}>{errorMessage.subTitle}</Box>
        <Button
          onClick={() => {
            analytics.track('Click Boton', {
              initiator: 'usuario',
              text: errorMessage.button.label,
            });
            if (errorMessage.button.href) {
              window.open(errorMessage.button.href, '_self');
            } else {
              errorMessage.button.action?.();
            }
          }}
          variant="contained"
          color="primary"
        >
          {errorMessage.button.label}
        </Button>
      </div>
      <SetPassword
        open={openAlertDialog}
        onSuccess={() => window.open('/account', '_self')}
        onError={() => setOpenAlertDialog(false)}
        token={token}
      />
    </div>
  );
}
